import type { Row, RowData } from "@tanstack/table-core";
import { VirtualItem, Virtualizer } from "@tanstack/virtual-core";
import type { KeyboardEvent } from "react";
import { cn } from "../../../utils/classes";
import { containerGridStyles } from "../utils/grid";
import { getStickyClasses } from "../utils/sticky";
import { TableBodyCell } from "./cell";

type VirtualTableBodyRowProps<D extends RowData> =
  | {
      rowVirtualizer: Virtualizer<HTMLDivElement, HTMLTableRowElement>;
      virtualRow: VirtualItem;
    }
  | {
      rowVirtualizer?: undefined;
      virtualRow?: undefined;
    };

type TableBodyRowProps<D extends RowData> = {
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>;
  row: Row<D>;
  virtualPaddingLeft: number | undefined;
  virtualPaddingRight: number | undefined;
  onRowClick?: (row: D) => void;
} & VirtualTableBodyRowProps<D>;

export function TableBodyRow<D extends RowData>({
  columnVirtualizer,
  row,
  rowVirtualizer,
  virtualPaddingLeft,
  virtualPaddingRight,
  virtualRow,
  onRowClick,
}: TableBodyRowProps<D>) {
  const visibleCells = row.getVisibleCells();
  const virtualColumns = columnVirtualizer.getVirtualItems();
  const clickable = !!onRowClick;
  return (
    <tr
      {...(virtualRow
        ? {
            "data-index": virtualRow.index, //needed for dynamic row height measurement
            ref: (node) => rowVirtualizer.measureElement(node), //measure dynamic row height
          }
        : {})}
      key={row.id}
      {...(clickable
        ? {
            onClick: () => onRowClick(row.original),
            role: "button" as const,
            tabIndex: 0,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick(row.original);
              }
            },
          }
        : {})}
      // The body cells paint their own bg-white, so a plain row hover wouldn't
      // show — `hover:[&>td]:bg-gray-50` repaints the cells on row hover.
      className={cn("items-stretch", {
        "cursor-pointer hover:[&>td]:bg-gray-50": clickable,
      })}
      style={{
        ...containerGridStyles,
        width: "100%",
        // TODO: Add more table densities and dynamic heights
        minHeight: "48px",
        // height: "min-content",
        alignItems: "center",
      }}
    >
      {virtualPaddingLeft ? (
        //fake empty column to the left for virtualization scroll padding
        <td
          style={{
            contentVisibility: "auto",
            containIntrinsicSize: "auto",
            display: "flex",
            width: virtualPaddingLeft,
          }}
          className={getStickyClasses("right")}
        />
      ) : null}
      {virtualColumns.map((vc) => {
        const cell = visibleCells[vc.index];
        return <TableBodyCell key={cell.id} cell={cell} />;
      })}
      {virtualPaddingRight ? (
        //fake empty column to the right for virtualization scroll padding
        <td
          style={{
            contentVisibility: "auto",
            containIntrinsicSize: "auto",
            display: "flex",
            width: virtualPaddingRight,
          }}
          className={cn("text-start font-medium", getStickyClasses("right"))}
        />
      ) : null}
    </tr>
  );
}
